import {
  AccountBalanceOutlined,
  AssessmentOutlined,
  AssignmentIndOutlined,
  CategoryOutlined,
  ConnectWithoutContactOutlined,
  EventOutlined,
  FactCheckOutlined,
  GavelOutlined,
  HistoryOutlined,
  LockOpenOutlined,
  PersonSearchOutlined,
  PaymentsOutlined,
  ReceiptLongOutlined,
  RequestPageOutlined,
  RequestQuoteOutlined,
  StickyNote2Outlined,
  TaskAltOutlined,
  TimerOutlined
} from '@mui/icons-material'
import { SvgIconComponent } from '@mui/icons-material'
import dayjs from 'dayjs'
import { ActivityEntityType, ActivityTrackingAction, FetchActivitiesQuery } from '../../../generated'

export type Activity = NonNullable<NonNullable<FetchActivitiesQuery['fetchActivities']['activities']>[number]>

interface ActivityTag {
  label: string
  color: string
}

// The broad kind of activity shown as a tag on each row, so billing work stands apart from notes,
// events and the rest at a glance.
const TAGS: Record<
  | 'matter'
  | 'billing'
  | 'payable'
  | 'entry'
  | 'note'
  | 'event'
  | 'task'
  | 'accounting'
  | 'report'
  | 'setup'
  | 'user'
  | 'conflict'
  | 'other',
  ActivityTag
> = {
  matter: { label: 'Matter', color: '#771117' },
  billing: { label: 'Billing', color: '#0074D4' },
  payable: { label: 'Payable', color: '#B54708' },
  entry: { label: 'Time & Expense', color: '#6941C6' },
  note: { label: 'Note', color: '#667085' },
  event: { label: 'Event', color: '#0E9384' },
  task: { label: 'Task', color: '#3E4784' },
  accounting: { label: 'Accounting', color: '#388277' },
  report: { label: 'Report', color: '#475467' },
  setup: { label: 'Setup', color: '#475467' },
  user: { label: 'User', color: '#C4320A' },
  conflict: { label: 'Conflict Check', color: '#C11574' },
  other: { label: 'Other', color: '#667085' }
}

interface ActivityTypeMeta {
  value: ActivityEntityType
  label: string
  Icon: SvgIconComponent
  tag: ActivityTag
}

// The activity types that happen against a matter, in the order the matter tab's filter lists them.
export const MATTER_ACTIVITY_TYPES: ActivityTypeMeta[] = [
  { value: ActivityEntityType.Matter, label: 'Matter', Icon: GavelOutlined, tag: TAGS.matter },
  { value: ActivityEntityType.Bill, label: 'Bill', Icon: ReceiptLongOutlined, tag: TAGS.billing },
  { value: ActivityEntityType.Payments, label: 'Payment', Icon: PaymentsOutlined, tag: TAGS.billing },
  { value: ActivityEntityType.PaymentRefund, label: 'Payment Refund', Icon: PaymentsOutlined, tag: TAGS.billing },
  { value: ActivityEntityType.AccountPayable, label: 'Payable', Icon: RequestQuoteOutlined, tag: TAGS.payable },
  { value: ActivityEntityType.Entry, label: 'Time / Expense Entry', Icon: TimerOutlined, tag: TAGS.entry },
  { value: ActivityEntityType.Notes, label: 'Note', Icon: StickyNote2Outlined, tag: TAGS.note },
  { value: ActivityEntityType.MatterEvents, label: 'Event', Icon: EventOutlined, tag: TAGS.event },
  { value: ActivityEntityType.UserMatterTask, label: 'Task Assignment', Icon: AssignmentIndOutlined, tag: TAGS.task }
]

// Firm-level activity, which only the firm-wide feed shows.
const FIRM_ACTIVITY_TYPES: ActivityTypeMeta[] = [
  { value: ActivityEntityType.FundRequest, label: 'Fund Request', Icon: RequestPageOutlined, tag: TAGS.billing },
  { value: ActivityEntityType.PayableRefund, label: 'Payable Refund', Icon: RequestQuoteOutlined, tag: TAGS.payable },
  { value: ActivityEntityType.Task, label: 'Task', Icon: TaskAltOutlined, tag: TAGS.task },
  { value: ActivityEntityType.Account, label: 'Account', Icon: AccountBalanceOutlined, tag: TAGS.accounting },
  { value: ActivityEntityType.Reconciliation, label: 'Reconciliation', Icon: FactCheckOutlined, tag: TAGS.accounting },
  { value: ActivityEntityType.Reports, label: 'Report', Icon: AssessmentOutlined, tag: TAGS.report },
  { value: ActivityEntityType.MatterType, label: 'Matter Type', Icon: CategoryOutlined, tag: TAGS.setup },
  { value: ActivityEntityType.LawType, label: 'Law Type', Icon: CategoryOutlined, tag: TAGS.setup },
  {
    value: ActivityEntityType.References,
    label: 'Referral Source',
    Icon: ConnectWithoutContactOutlined,
    tag: TAGS.setup
  },
  { value: ActivityEntityType.User, label: 'User', Icon: LockOpenOutlined, tag: TAGS.user },
  { value: ActivityEntityType.ConflictCheck, label: 'Conflict Check', Icon: PersonSearchOutlined, tag: TAGS.conflict }
]

/** Every activity type, matter-level first; the firm-wide feed's filter lists them all. */
export const ACTIVITY_TYPES = [...MATTER_ACTIVITY_TYPES, ...FIRM_ACTIVITY_TYPES]

const FALLBACK_TYPE: Omit<ActivityTypeMeta, 'value'> = { label: 'Activity', Icon: HistoryOutlined, tag: TAGS.other }

export const getActivityType = (entityType?: ActivityEntityType | null) =>
  ACTIVITY_TYPES.find(({ value }) => value === entityType) ?? FALLBACK_TYPE

/** Whether a value (say, from the URL) is an activity type the API accepts as a filter. */
export const isActivityEntityType = (value?: string | null): value is ActivityEntityType =>
  Object.values(ActivityEntityType).includes(value as ActivityEntityType)

// Headings per kind of activity, grouped like the API's enum. Left unannotated on purpose: each group keeps
// its exact keys, so ACTION_LABELS below still fails to compile when the API adds an action nobody labelled.

// Matter
const MATTER_ACTION_LABELS = {
  [ActivityTrackingAction.CreatedMatter]: 'Created matter',
  [ActivityTrackingAction.UpdatedMatter]: 'Updated matter',
  [ActivityTrackingAction.ClosedMatter]: 'Closed matter',
  [ActivityTrackingAction.ReopenedMatter]: 'Reopened matter',
  [ActivityTrackingAction.ConvertedConsultToMatter]: 'Converted consult to matter',
  [ActivityTrackingAction.ConvertedFlatFeeToHourly]: 'Converted flat fee to hourly',
  [ActivityTrackingAction.ArchivedMatter]: 'Archived matter',
  [ActivityTrackingAction.DeletedMatter]: 'Deleted matter'
}

// Matter notes
const NOTE_ACTION_LABELS = {
  [ActivityTrackingAction.CreatedMatterNotes]: 'Added a note',
  [ActivityTrackingAction.UpdatedMatterNotes]: 'Edited a note',
  [ActivityTrackingAction.DeletedMatterNotes]: 'Deleted a note'
}

// Matter events
const EVENT_ACTION_LABELS = {
  [ActivityTrackingAction.CreatedCaseEvent]: 'Added an event',
  [ActivityTrackingAction.UpdatedCaseEvent]: 'Updated an event',
  [ActivityTrackingAction.DeletedCaseEvent]: 'Deleted an event'
}

// Tasks and task assignments
const TASK_ACTION_LABELS = {
  [ActivityTrackingAction.CreatedCaseTask]: 'Created a task',
  [ActivityTrackingAction.UpdatedCaseTask]: 'Updated a task',
  [ActivityTrackingAction.DeletedCaseTask]: 'Deleted a task',
  [ActivityTrackingAction.AssignedSomeoneToTask]: 'Assigned a task',
  [ActivityTrackingAction.RemovedUserFromTask]: 'Removed a task assignee'
}

// Time and expense entries
const ENTRY_ACTION_LABELS = {
  [ActivityTrackingAction.CreatedEntry]: 'Added an entry',
  [ActivityTrackingAction.UpdatedEntry]: 'Edited an entry',
  [ActivityTrackingAction.DeletedEntry]: 'Deleted an entry'
}

// Bills
const BILL_ACTION_LABELS = {
  [ActivityTrackingAction.CreatedBill]: 'Created a bill',
  [ActivityTrackingAction.UpdatedBill]: 'Edited a bill',
  [ActivityTrackingAction.SentBillsToAttorney]: 'Sent bill for attorney approval',
  [ActivityTrackingAction.ApprovedBill]: 'Approved bill',
  [ActivityTrackingAction.FinalizedBill]: 'Finalized bill',
  [ActivityTrackingAction.SentBillToClient]: 'Sent bill to client',
  [ActivityTrackingAction.ResendBillToClient]: 'Resent bill to client',
  [ActivityTrackingAction.FailedToSendBillToClient]: 'Failed to send bill to client',
  [ActivityTrackingAction.SentReminderToClient]: 'Sent payment reminder to client',
  [ActivityTrackingAction.FailedToSendReminderToClient]: 'Failed to send payment reminder',
  [ActivityTrackingAction.MarkedBillAsDelivered]: 'Marked bill as delivered',
  [ActivityTrackingAction.UpdatedBillDueDate]: 'Updated bill due date',
  [ActivityTrackingAction.UpdatedBillStatus]: 'Updated bill status',
  [ActivityTrackingAction.CarriedForwardBill]: 'Carried balance forward',
  [ActivityTrackingAction.WroteOffBill]: 'Wrote off bill',
  [ActivityTrackingAction.VoidedBill]: 'Voided bill',
  [ActivityTrackingAction.ArchivedBill]: 'Archived bill'
}

// Interest
const INTEREST_ACTION_LABELS = {
  [ActivityTrackingAction.AppliedInterest]: 'Applied interest',
  [ActivityTrackingAction.DeletedInterest]: 'Deleted interest entries',
  [ActivityTrackingAction.SwitchedInterestRate]: 'Switched interest rate'
}

// Fund requests
const FUND_REQUEST_ACTION_LABELS = {
  [ActivityTrackingAction.CreatedFundRequest]: 'Created a fund request',
  [ActivityTrackingAction.UpdatedFundRequest]: 'Updated a fund request',
  [ActivityTrackingAction.AppliedClientFunds]: 'Applied client funds'
}

// Payments and refunds
const PAYMENT_ACTION_LABELS = {
  [ActivityTrackingAction.RecordedManualPayment]: 'Recorded a payment',
  [ActivityTrackingAction.ClientPaidOnline]: 'Client paid online',
  [ActivityTrackingAction.RecordedClientCredit]: 'Recorded client credit',
  [ActivityTrackingAction.AppliedCredits]: 'Applied credit to bill',
  [ActivityTrackingAction.UpdatedPayment]: 'Edited a payment',
  [ActivityTrackingAction.ReversedPayment]: 'Reversed a payment',
  [ActivityTrackingAction.PaymentStatusUpdatedByProcessor]: 'Payment processor updated status',
  [ActivityTrackingAction.MarkedPaymentLegacyReconciled]: 'Marked payment as legacy reconciled',
  [ActivityTrackingAction.CreateFirmPayment]: 'Recorded a firm payment',
  [ActivityTrackingAction.TransferredFundsBetweenAccounts]: 'Transferred funds between accounts',
  [ActivityTrackingAction.CreatedPaymentRefund]: 'Refunded a payment',
  [ActivityTrackingAction.ReturnedCheck]: 'Returned check'
}

// Payables and checks
const PAYABLE_ACTION_LABELS = {
  [ActivityTrackingAction.CreatedAccountPayable]: 'Created a payable',
  [ActivityTrackingAction.UpdatedAccountPayable]: 'Edited a payable',
  [ActivityTrackingAction.MarkedVoucherAsPaid]: 'Marked payable as paid',
  [ActivityTrackingAction.MarkedPayableAsPaid]: 'Marked payable as paid',
  [ActivityTrackingAction.VoidPayable]: 'Voided payable',
  [ActivityTrackingAction.VoidCheck]: 'Voided check',
  [ActivityTrackingAction.VoidAndReissueCheck]: 'Voided and reissued check',
  [ActivityTrackingAction.CreatedRefundForPayable]: 'Recorded a payable refund'
}

// Accounts and reconciliation
const ACCOUNTING_ACTION_LABELS = {
  [ActivityTrackingAction.CreatedAccount]: 'Created an account',
  [ActivityTrackingAction.UpdatedAccount]: 'Updated an account',
  [ActivityTrackingAction.ArchivedAccount]: 'Archived/unarchived an account',
  [ActivityTrackingAction.ReconcilePayments]: 'Reconciled payments',
  [ActivityTrackingAction.UndoReconciliation]: 'Undid reconciliation'
}

// Reports
const REPORT_ACTION_LABELS = {
  [ActivityTrackingAction.RequestedReport]: 'Requested a report',
  [ActivityTrackingAction.RetriedReport]: 'Retried a report',
  [ActivityTrackingAction.CancelledReport]: 'Cancelled a report'
}

// Setup: matter types, law types, referral sources
const SETUP_ACTION_LABELS = {
  [ActivityTrackingAction.CreatedMatterType]: 'Created a matter type',
  [ActivityTrackingAction.UpdatedMatterType]: 'Updated a matter type',
  [ActivityTrackingAction.DeletedMatterType]: 'Deleted a matter type',
  [ActivityTrackingAction.CreatedLawType]: 'Created a law type',
  [ActivityTrackingAction.UpdatedLawType]: 'Updated a law type',
  [ActivityTrackingAction.DeletedLawType]: 'Deleted a law type',
  [ActivityTrackingAction.CreatedReference]: 'Added a referral source',
  [ActivityTrackingAction.UpdatedReference]: 'Updated a referral source'
}

// Users
const USER_ACTION_LABELS = {
  [ActivityTrackingAction.UnlockedUser]: 'Unlocked a user account'
}

// Contacts
const CONTACT_ACTION_LABELS = {
  [ActivityTrackingAction.RanConflictCheck]: 'Ran a conflict check'
}

// No longer logged, but older rows carry them
const LEGACY_ACTION_LABELS = {
  [ActivityTrackingAction.GeneratedPreBills]: 'Generated pre-bills',
  [ActivityTrackingAction.GeneratedPreBillsForAllAttorneys]: 'Generated pre-bills for all attorneys',
  [ActivityTrackingAction.GeneratedQuickBill]: 'Generated a quick bill',
  [ActivityTrackingAction.AttorneyApprovedBills]: 'Attorney approved bill',
  [ActivityTrackingAction.DepartmentHeadApprovedBills]: 'Department head approved bill',
  [ActivityTrackingAction.PerformedBulkAction]: 'Performed a bulk bill action',
  [ActivityTrackingAction.UpdatedAssigneesOfTask]: 'Updated task assignees',
  [ActivityTrackingAction.ActivatedOrDeactivatedReference]: 'Activated/deactivated a referral source',
  [ActivityTrackingAction.DeletedReference]: 'Deleted a referral source'
}

// A heading for every action.
export const ACTION_LABELS: Record<ActivityTrackingAction, string> = {
  ...MATTER_ACTION_LABELS,
  ...NOTE_ACTION_LABELS,
  ...EVENT_ACTION_LABELS,
  ...TASK_ACTION_LABELS,
  ...ENTRY_ACTION_LABELS,
  ...BILL_ACTION_LABELS,
  ...INTEREST_ACTION_LABELS,
  ...FUND_REQUEST_ACTION_LABELS,
  ...PAYMENT_ACTION_LABELS,
  ...PAYABLE_ACTION_LABELS,
  ...ACCOUNTING_ACTION_LABELS,
  ...REPORT_ACTION_LABELS,
  ...SETUP_ACTION_LABELS,
  ...USER_ACTION_LABELS,
  ...CONTACT_ACTION_LABELS,
  ...LEGACY_ACTION_LABELS
}

/**
 * The stored description begins with a " - " separator meant to follow the action. Inside a single
 * matter, most entities also repeat "for (#matter) title", which `hideMatterReference` drops.
 */
export const formatActivityDescription = (
  description?: string | null,
  entityType?: ActivityEntityType | null,
  hideMatterReference = false
) => {
  let text = (description ?? '').replace(/^\s*-\s*/, '').trim()
  if (hideMatterReference && entityType !== ActivityEntityType.Matter) text = text.replace(/\s+for \(#[^)]*\).*$/, '')
  if (entityType === ActivityEntityType.Bill) text = text.replace(/^INV#(\d+)/, 'Invoice #$1')
  return text
}

// A deleted user's rows lose `creator` but keep `actorName`; only rows with neither are the system's.
export const activityUserName = ({ creator, actorName }: Activity) =>
  creator ? `${creator.firstName ?? ''} ${creator.lastName ?? ''}`.trim() || 'Unknown user' : actorName || 'System'

const dayLabel = (date: dayjs.Dayjs) => {
  if (date.isSame(dayjs(), 'day')) return 'Today'
  if (date.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Yesterday'
  return date.format('MMM D, YYYY')
}

/** Splits a page of activities (newest first) into runs that happened on the same day. */
export const groupActivitiesByDay = (activities: Activity[]) =>
  activities.reduce<{ label: string; key: string; items: Activity[] }[]>((groups, activity) => {
    const date = dayjs(activity.createdAt)
    const key = date.format('YYYY-MM-DD')
    const last = groups[groups.length - 1]

    if (last?.key === key) last.items.push(activity)
    else groups.push({ key, label: dayLabel(date), items: [activity] })
    return groups
  }, [])
